/**
 * TRX Payments API Client
 *
 * Client-side service for TRX-specific payment operations on apps/api.
 * Today this surfaces only the PayPage session-mint call consumed by
 * `apps/checkout`'s `TrxJsLibraryInput` component (PR 4C); future TRX-
 * specific endpoints (e.g. PR 5A's webhook health check) join here.
 *
 * Both apps/checkout and the admin app reuse the same axios client with App Check + bearer-token
 * + automation-bypass interceptors.
 */

import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('TrxPaymentsAPI');

const apiClient = createApiClient('/api/v1/payments');

// ─────────────────────────── PayPage session mint ──────────────────────

export type TrxPayPageInnerAction = 'Sale' | 'Auth' | 'Avs' | 'Verify';
export type TrxPayPagePaymentType = 'Card' | 'Ach' | 'Wallet';

export interface TrxPayPageSessionRequest {
    readonly tenantId: number;
    readonly restaurantId: number;
    /** Defaults to "Sale" server-side. */
    readonly innerAction?: TrxPayPageInnerAction;
    /** Required server-side when innerAction is Sale or Auth. */
    readonly amount?: {
        readonly minorUnits: number;
        readonly currency: 'USD' | 'CAD';
    };
    /** Echoes onto Reference.PurchaseId for orderId tracking. */
    readonly purchaseId?: string;
    /** Echoes onto Reference.Guid for inbound-webhook dedup. */
    readonly idempotencyKey?: string;
    /** Filter rails the JS library surfaces. Up to 4 per §10.5. */
    readonly paymentTypes?: readonly TrxPayPagePaymentType[];
    /** Session expiry in minutes. Defaults to TRX admin-config default. */
    readonly timeToLiveMinutes?: number;
}

/**
 * Server response — the JS library's `sessionData` triple plus
 * metadata. `clientId` and `payPageId` are env-sourced server-side and
 * surfaced here so the browser never reads TRX env directly. `sessionId`
 * is freshly minted by TRX per request.
 */
export interface TrxPayPageSessionResponse {
    readonly clientId: string;
    readonly payPageId: string;
    readonly sessionId: string;
    readonly expirationTime?: string;
    readonly response: {
        readonly category: string;
        readonly providerCode: string;
        readonly providerText: string;
    };
}


/**
 * Mint a TRX PayPage session for the JS library. The returned triple
 * is consumed by `PayPageJS.initialize({ sessionData: ... })` per §11.2
 * of the TRX API reference.
 *
 * Server validates that the restaurant is configured for TRX before
 * minting — this client doesn't need to pre-check; it'll surface a 400
 * with a clear message if the restaurant routes to another processor.
 */
export const initPayPageSession = async (
    request: TrxPayPageSessionRequest,
): Promise<TrxPayPageSessionResponse> => {
    logger.debug('Minting TRX PayPage session', {
        tenantId: request.tenantId,
        restaurantId: request.restaurantId,
        innerAction: request.innerAction ?? 'Sale',
    });

    const response = await apiClient.post<TrxPayPageSessionResponse>(
        '/trx/paypage/init',
        request,
    );

    logger.info('TRX PayPage session minted', {
        sessionId: (response as unknown as TrxPayPageSessionResponse).sessionId,
        category: (response as unknown as TrxPayPageSessionResponse).response.category,
    });

    return response as unknown as TrxPayPageSessionResponse;
};

export const trxPaymentsApiService = {
    initPayPageSession,
};
